import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./DolibarrCustomerMapping.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

axios.defaults.withCredentials = true;

const ITEMS_PER_PAGE = 40;

const DolibarrCustomerMapping = () => {
  const [dolibarrCustomers, setDolibarrCustomers] = useState([]);
  const [ticketCustomers, setTicketCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [mappingFilter, setMappingFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showDeleteMappingConfirm, setShowDeleteMappingConfirm] =
    useState(false);
  const [selectedDeleteMapping, setSelectedDeleteMapping] = useState(null);
  const [deleteMappingMessage, setDeleteMappingMessage] = useState("");
  const [deleteMappingMessageType, setDeleteMappingMessageType] = useState("");
  const [isDeletingMapping, setIsDeletingMapping] = useState(false);
  const [showSaveMappingConfirm, setShowSaveMappingConfirm] = useState(false);
  const [selectedSaveMappingCustomer, setSelectedSaveMappingCustomer] =
    useState(null);
  const [saveMappingMessage, setSaveMappingMessage] = useState("");
  const [saveMappingMessageType, setSaveMappingMessageType] = useState("");
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [selectedBulkMap, setSelectedBulkMap] = useState({});

  useEffect(() => {
    fetchDolibarrCustomers();
    fetchTicketCustomers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, mappingFilter]);

  const fetchDolibarrCustomers = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `/api/dolibarr/mapping/customers`,
        {
          withCredentials: true,
        },
      );

      const rows = Array.isArray(res.data) ? res.data : [];
      setDolibarrCustomers(rows);
    } catch (err) {
      console.error("fetchDolibarrCustomers error:", err);
      alert(err.response?.data?.error || "Failed to load Dolibarr customers");
    } finally {
      setLoading(false);
    }
  };
  const fetchTicketCustomers = async () => {
    try {
      const res = await axios.get(
        `/api/dolibarr/mapping/ticket-customers`,
        {
          withCredentials: true,
        },
      );

      setTicketCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("fetchTicketCustomers error:", err);
      alert(err.response?.data?.error || "Failed to load ticket customers");
    }
  };

  const filteredDolibarrCustomers = useMemo(() => {
    const keyword = search.toLowerCase().trim();

    return dolibarrCustomers.filter((item) => {
      const isMapped = Number(item.is_mapped) === 1;

      if (mappingFilter === "mapped" && !isMapped) {
        return false;
      }

      if (mappingFilter === "not_mapped" && isMapped) {
        return false;
      }

      if (!keyword) {
        return true;
      }

      return (
        String(item.contract_ref || "")
          .toLowerCase()
          .includes(keyword) ||
        String(item.cus_name || "")
          .toLowerCase()
          .includes(keyword) ||
        (Array.isArray(item.mapped_users) &&
          item.mapped_users.some((user) =>
            String(user.customer_name || "")
              .toLowerCase()
              .includes(keyword),
          ))
      );
    });
  }, [search, mappingFilter, dolibarrCustomers]);

  const totalPages = Math.ceil(
    filteredDolibarrCustomers.length / ITEMS_PER_PAGE,
  );

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

  const paginatedDolibarrCustomers = filteredDolibarrCustomers.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const handleSaveMapping = (dolibarrCustomer) => {
    const selectedTicketIds = selectedBulkMap[dolibarrCustomer.cus_id] || [];

    setSaveMappingMessage("");
    setSaveMappingMessageType("");

    if (selectedTicketIds.length === 0) {
      setSaveMappingMessage("Please select customer before save.");
      setSaveMappingMessageType("error");

      setTimeout(() => {
        setSaveMappingMessage("");
        setSaveMappingMessageType("");
      }, 3000);

      return;
    }

    setSelectedSaveMappingCustomer(dolibarrCustomer);
    setShowSaveMappingConfirm(true);
  };

  const confirmSaveMapping = async () => {
    if (!selectedSaveMappingCustomer) return;

    const dolibarrCustomer = selectedSaveMappingCustomer;
    const selectedTicketIds = selectedBulkMap[dolibarrCustomer.cus_id] || [];

    if (selectedTicketIds.length === 0) {
      setSaveMappingMessage("Please select customer before save.");
      setSaveMappingMessageType("error");
      return;
    }

    setIsSavingMapping(true);
    setSaveMappingMessage("");
    setSaveMappingMessageType("");

    try {
      await axios.post(
        "/api/dolibarr/mapping/bulk",
        {
          cus_dolibarr_id: dolibarrCustomer.cus_id,
          cus_ticket_ids: selectedTicketIds,
        },
        {
          withCredentials: true,
        },
      );

      setShowSaveMappingConfirm(false);
      setSelectedSaveMappingCustomer(null);

      setSelectedBulkMap((prev) => ({
        ...prev,
        [dolibarrCustomer.cus_id]: [],
      }));

      setSaveMappingMessage("Mapping saved successfully.");
      setSaveMappingMessageType("success");

      fetchDolibarrCustomers();

      setTimeout(() => {
        setSaveMappingMessage("");
        setSaveMappingMessageType("");
      }, 3000);
    } catch (err) {
      console.error("handleSaveMapping error:", err);

      setSaveMappingMessage(
        err.response?.data?.error || "Failed to save mapping.",
      );
      setSaveMappingMessageType("error");
    } finally {
      setIsSavingMapping(false);
    }
  };

  const cancelSaveMapping = () => {
    setShowSaveMappingConfirm(false);
    setSelectedSaveMappingCustomer(null);
    setSaveMappingMessage("");
    setSaveMappingMessageType("");
  };

  const handleDeleteMapping = (dolibarrCusId, mappedUser) => {
    setSelectedDeleteMapping({
      cus_dolibarr_id: dolibarrCusId,
      cus_ticket_id: mappedUser.cus_ticket_id,
      customer_name: mappedUser.customer_name,
    });

    setDeleteMappingMessage("");
    setDeleteMappingMessageType("");
    setShowDeleteMappingConfirm(true);
  };

  const confirmDeleteMapping = async () => {
    if (!selectedDeleteMapping) return;

    setIsDeletingMapping(true);
    setDeleteMappingMessage("");
    setDeleteMappingMessageType("");

    try {
      await axios.delete(
        `/api/dolibarr/mapping/${selectedDeleteMapping.cus_dolibarr_id}/${selectedDeleteMapping.cus_ticket_id}`,
        {
          withCredentials: true,
        },
      );

      setShowDeleteMappingConfirm(false);
      setSelectedDeleteMapping(null);

      setDeleteMappingMessage("Mapping deleted successfully.");
      setDeleteMappingMessageType("success");

      fetchDolibarrCustomers();

      setTimeout(() => {
        setDeleteMappingMessage("");
        setDeleteMappingMessageType("");
      }, 3000);
    } catch (err) {
      console.error("handleDeleteMapping error:", err);

      setDeleteMappingMessage(
        err.response?.data?.error || "Failed to delete mapping.",
      );
      setDeleteMappingMessageType("error");
    } finally {
      setIsDeletingMapping(false);
    }
  };

  const cancelDeleteMapping = () => {
    setShowDeleteMappingConfirm(false);
    setSelectedDeleteMapping(null);
    setDeleteMappingMessage("");
    setDeleteMappingMessageType("");
  };

  const handleCheckCustomer = (dolibarrCusId, customerId) => {
    setSelectedBulkMap((prev) => {
      const currentSelected = prev[dolibarrCusId] || [];
      const customerIdText = String(customerId);

      const isAlreadySelected = currentSelected.includes(customerIdText);

      return {
        ...prev,
        [dolibarrCusId]: isAlreadySelected
          ? currentSelected.filter((id) => id !== customerIdText)
          : [...currentSelected, customerIdText],
      };
    });
  };

  return (
    <div>
      <div className="dolibarr-mapping-card">
        <h2 className="cus-info-title">Dolibarr Customer Mapping</h2>
        <div className="dolibarr-mapping-toolbar">
          <input
            type="text"
            className="dolibarr-search-input"
            placeholder="Search ....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="mapping-filter-buttons">
            <button
              type="button"
              className={`mapping-filter-button ${
                mappingFilter === "all" ? "active" : ""
              }`}
              onClick={() => setMappingFilter("all")}
            >
              All
            </button>

            <button
              type="button"
              className={`mapping-filter-button ${
                mappingFilter === "not_mapped" ? "active" : ""
              }`}
              onClick={() => setMappingFilter("not_mapped")}
            >
              Not Mapped
            </button>

            <button
              type="button"
              className={`mapping-filter-button ${
                mappingFilter === "mapped" ? "active" : ""
              }`}
              onClick={() => setMappingFilter("mapped")}
            >
              Mapped
            </button>
          </div>
        </div>
        <p className="dolibarr-mapping-note">
          *กรณีไม่พบ customer ที่ต้องการ Map
          กรุณาให้ผู้ใช้ลงทะเบียนสมัครสมาชิกผ่านระบบก่อนดำเนินการ
        </p>{" "}
        {loading ? (
          <div className="dolibarr-loading">Loading...</div>
        ) : (
          <div className="dolibarr-table-wrap">
            <table className="dolibarr-mapping-table">
              <thead>
                <tr>
                  {/* <th>Contract Ref</th> */}
                  <th>Dolibarr Customer Name</th>
                  <th>Add Ticket Customer</th>
                  <th>Selected Users</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedDolibarrCustomers.length > 0 ? (
                  paginatedDolibarrCustomers.map((item) => (
                    <tr key={item.cus_id}>
                      {/* <td className="dolibarr-cus-id">{item.contract_ref}</td> */}

                      <td className="dolibarr-cus-name">
                        {item.cus_name || "-"}
                      </td>
                      <td>
                        <div className="dolibarr-checkbox-list">
                          {ticketCustomers.map((customer) => {
                            const customerId = String(customer.customer_id);

                            const isChecked =
                              selectedBulkMap[item.cus_id]?.includes(
                                customerId,
                              ) || false;

                            const isAlreadyMapped =
                              Array.isArray(item.mapped_users) &&
                              item.mapped_users.some(
                                (user) =>
                                  String(user.cus_ticket_id) === customerId,
                              );

                            return (
                              <label
                                key={customer.customer_id}
                                className={`dolibarr-checkbox-item ${
                                  isAlreadyMapped ? "already-mapped" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked || isAlreadyMapped}
                                  disabled={isAlreadyMapped}
                                  onChange={() =>
                                    handleCheckCustomer(
                                      item.cus_id,
                                      customer.customer_id,
                                    )
                                  }
                                />

                                <span
                                  title={`${customer.customer_name || "-"} / ${
                                    customer.company_name || "No Company"
                                  }`}
                                >
                                  {customer.customer_name || "-"} /{" "}
                                  {customer.company_name || "No Company"}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </td>

                      <td>
                        {Array.isArray(item.mapped_users) &&
                        item.mapped_users.length > 0 ? (
                          <div className="mapped-user-list">
                            {item.mapped_users.map((user) => (
                              <div
                                key={`${item.cus_id}-${user.cus_ticket_id}`}
                                className="mapped-user-item"
                              >
                                <span>{user.customer_name || "-"}</span>

                                <button
                                  type="button"
                                  className="mapping-remove-small-button"
                                  onClick={() =>
                                    handleDeleteMapping(item.cus_id, user)
                                  }
                                  title="Remove"
                                  aria-label="Remove mapping"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span>-</span>
                        )}
                      </td>

                      <td>
                        {item.is_mapped === 1 ? (
                          <span className="mapping-status mapped">Mapped</span>
                        ) : (
                          <span className="mapping-status not-mapped">
                            Not mapped
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="mapping-actions">
                          <button
                            type="button"
                            className="mapping-save-button"
                            onClick={() => handleSaveMapping(item)}
                          >
                            Save
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="dolibarr-empty">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="ticket-pagination">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;

                  return (
                    <button
                      type="button"
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page ? "active-page" : ""}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {showDeleteMappingConfirm && (
        <div className="mapping-modal-overlay">
          <div className="mapping-confirm-modal">
            <h3>ยืนยันการลบ Mapping</h3>

            <p>
              ต้องการลบ user{" "}
              <strong>{selectedDeleteMapping?.customer_name || "-"}</strong>{" "}
              ออกจาก mapping นี้ใช่หรือไม่?
            </p>

            {deleteMappingMessage && (
              <div
                className={`mapping-popup-message ${deleteMappingMessageType}`}
              >
                {deleteMappingMessage}
              </div>
            )}

            <div className="mapping-confirm-actions">
              <button
                type="button"
                className="mapping-confirm-danger-btn"
                onClick={confirmDeleteMapping}
                disabled={isDeletingMapping}
              >
                {isDeletingMapping ? "กำลังลบ..." : "ยืนยันลบ"}
              </button>

              <button
                type="button"
                className="mapping-confirm-cancel-btn"
                onClick={cancelDeleteMapping}
                disabled={isDeletingMapping}
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteMappingMessage && !showDeleteMappingConfirm && (
        <div className="mapping-toast-wrapper">
          <div className={`mapping-toast-message ${deleteMappingMessageType}`}>
            {deleteMappingMessage}
          </div>
        </div>
      )}
      {showSaveMappingConfirm && (
        <div className="mapping-modal-overlay">
          <div className="mapping-confirm-modal">
            <h3>ยืนยันการบันทึก Mapping</h3>

            <p>ต้องการบันทึก mapping นี้ใช่หรือไม่?</p>

            {saveMappingMessage && (
              <div
                className={`mapping-popup-message ${saveMappingMessageType}`}
              >
                {saveMappingMessage}
              </div>
            )}

            <div className="mapping-confirm-actions">
              <button
                type="button"
                className="mapping-confirm-save-btn"
                onClick={confirmSaveMapping}
                disabled={isSavingMapping}
              >
                {isSavingMapping ? "กำลังบันทึก..." : "ยืนยันบันทึก"}
              </button>

              <button
                type="button"
                className="mapping-confirm-cancel-btn"
                onClick={cancelSaveMapping}
                disabled={isSavingMapping}
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {saveMappingMessage && !showSaveMappingConfirm && (
        <div className="mapping-toast-wrapper">
          <div className={`mapping-toast-message ${saveMappingMessageType}`}>
            {saveMappingMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default DolibarrCustomerMapping;
